import React, { memo, useState, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { contactsAPI, companiesAPI, dealsAPI } from '../../api';

// Simple Ticket Create Dialog (uses internal form management for performance)
export const SimpleTicketDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  onSuccess 
}) => {
  const [form, setForm] = useState({
    subject: '',
    type: 'support',
    priority: 'medium',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await contactsAPI.createTicket(contactId, form);
      toast.success('Ticket created successfully');
      setForm({ subject: '', type: 'support', priority: 'medium', description: '' });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, onOpenChange, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Ticket</DialogTitle>
          <DialogDescription>Create a support ticket for this contact</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Subject</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm(p => ({ ...p, subject: e.target.value }))}
              placeholder="Brief description of the issue"
              required
              data-testid="simple-ticket-subject-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm(p => ({ ...p, type: v }))}>
                <SelectTrigger data-testid="simple-ticket-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="billing">Billing</SelectItem>
                  <SelectItem value="feature_request">Feature Request</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm(p => ({ ...p, priority: v }))}>
                <SelectTrigger data-testid="simple-ticket-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Detailed description of the issue"
              rows={4}
              data-testid="simple-ticket-description-input"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="simple-ticket-submit-btn">
              {loading ? 'Creating...' : 'Create Ticket'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});
SimpleTicketDialog.displayName = 'SimpleTicketDialog';

// Subscription Create Dialog
export const SimpleSubscriptionDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  onSuccess 
}) => {
  const [form, setForm] = useState({
    plan_name: '',
    price: '',
    billing_cycle: 'monthly',
    start_date: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await contactsAPI.createSubscription(contactId, {
        ...form,
        price: parseFloat(form.price) || 0
      });
      toast.success('Subscription added successfully');
      setForm({ plan_name: '', price: '', billing_cycle: 'monthly', start_date: new Date().toISOString().split('T')[0] });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to add subscription');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, onOpenChange, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Subscription</DialogTitle>
          <DialogDescription>Create a new subscription for this contact</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Plan Name</Label>
            <Input
              value={form.plan_name}
              onChange={(e) => setForm(p => ({ ...p, plan_name: e.target.value }))}
              placeholder="e.g., Professional Plan, Enterprise"
              required
              data-testid="simple-subscription-plan-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Price</Label>
              <Input
                type="number"
                step="0.01"
                value={form.price}
                onChange={(e) => setForm(p => ({ ...p, price: e.target.value }))}
                required
                data-testid="simple-subscription-price-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Billing Cycle</Label>
              <Select value={form.billing_cycle} onValueChange={(v) => setForm(p => ({ ...p, billing_cycle: v }))}>
                <SelectTrigger data-testid="simple-subscription-cycle-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Start Date</Label>
            <Input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm(p => ({ ...p, start_date: e.target.value }))}
              data-testid="simple-subscription-date-input"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="simple-subscription-submit-btn">
              {loading ? 'Adding...' : 'Add Subscription'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});
SimpleSubscriptionDialog.displayName = 'SimpleSubscriptionDialog';

// Attachment Upload Dialog
export const AttachmentDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  onSuccess 
}) => {
  const [form, setForm] = useState({ file: null, description: '' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm(p => ({ ...p, file }));
    }
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.file) return;
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', form.file);
      formData.append('description', form.description);
      await contactsAPI.uploadAttachment(contactId, formData);
      toast.success('File uploaded successfully');
      setForm({ file: null, description: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  }, [form, contactId, onOpenChange, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Attachment</DialogTitle>
          <DialogDescription>Upload a file for this contact</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Select File</Label>
            <Input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="cursor-pointer"
              data-testid="attachment-file-input"
            />
            {form.file && (
              <p className="text-sm text-muted-foreground">
                Selected: {form.file.name} ({(form.file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Brief description of the attachment"
              rows={2}
              data-testid="attachment-description-input"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={uploading || !form.file} data-testid="attachment-submit-btn">
              {uploading ? 'Uploading...' : 'Upload File'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});
AttachmentDialog.displayName = 'AttachmentDialog';

// Payment Record Dialog
export const SimplePaymentDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  onSuccess 
}) => {
  const [form, setForm] = useState({
    amount: '',
    method: 'credit_card',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await contactsAPI.createPayment(contactId, {
        ...form,
        amount: parseFloat(form.amount) || 0
      });
      toast.success('Payment recorded successfully');
      setForm({ amount: '', method: 'credit_card', description: '' });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to record payment');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, onOpenChange, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>Record a payment from this contact</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Amount</Label>
            <Input
              type="number"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))}
              required
              data-testid="simple-payment-amount-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Payment Method</Label>
            <Select value={form.method} onValueChange={(v) => setForm(p => ({ ...p, method: v }))}>
              <SelectTrigger data-testid="simple-payment-method-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="credit_card">Credit Card</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="e.g., Invoice #123, Q1 2025 subscription"
              rows={2}
              data-testid="simple-payment-description-input"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="simple-payment-submit-btn">
              {loading ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});
SimplePaymentDialog.displayName = 'SimplePaymentDialog';

// Company Create Dialog
export const CompanyDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  onSuccess 
}) => {
  const [form, setForm] = useState({
    name: '',
    industry: '',
    phone: '',
    website: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      // Create company first
      const companyRes = await companiesAPI.create({
        name: form.name,
        industry: form.industry || undefined,
        phone: form.phone || undefined,
        website: form.website || undefined,
        address: form.address || undefined
      });
      // Link to contact
      await contactsAPI.update(contactId, { company_id: companyRes.data.id });
      toast.success(`Company "${form.name}" created and linked to contact`);
      setForm({ name: '', industry: '', phone: '', website: '', address: '' });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to create company');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, onOpenChange, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Company</DialogTitle>
          <DialogDescription>Create a new company and link it to this contact</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Company Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Acme Inc."
              required
              data-testid="company-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Industry</Label>
              <Select value={form.industry || "none"} onValueChange={(v) => setForm(p => ({ ...p, industry: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="company-industry-select"><SelectValue placeholder="Select industry" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select...</SelectItem>
                  <SelectItem value="Technology">Technology</SelectItem>
                  <SelectItem value="Healthcare">Healthcare</SelectItem>
                  <SelectItem value="Finance">Finance</SelectItem>
                  <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                  <SelectItem value="Retail">Retail</SelectItem>
                  <SelectItem value="Services">Services</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="+1 555-123-4567"
                data-testid="company-phone-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Website</Label>
            <Input
              value={form.website}
              onChange={(e) => setForm(p => ({ ...p, website: e.target.value }))}
              placeholder="https://example.com"
              data-testid="company-website-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Address</Label>
            <Textarea
              value={form.address}
              onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))}
              placeholder="123 Main St, City, State"
              rows={2}
              data-testid="company-address-input"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="company-submit-btn">
              {loading ? 'Creating...' : 'Create Company'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});
CompanyDialog.displayName = 'CompanyDialog';

// Deal Create Dialog
export const SimpleDealDialog = memo(({ 
  open, 
  onOpenChange, 
  contactId,
  onSuccess 
}) => {
  const [form, setForm] = useState({
    title: '',
    value: '',
    stage: 'prospecting',
    expected_close_date: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await dealsAPI.create({
        title: form.title,
        contact_id: contactId,
        value: parseFloat(form.value) || 0,
        stage: form.stage,
        expected_close_date: form.expected_close_date || undefined
      });
      toast.success('Deal created successfully');
      setForm({ title: '', value: '', stage: 'prospecting', expected_close_date: '' });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Failed to create deal');
    } finally {
      setLoading(false);
    }
  }, [form, contactId, onOpenChange, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Deal</DialogTitle>
          <DialogDescription>Create a new deal for this contact</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Deal Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="Enterprise software license"
              required
              data-testid="simple-deal-title-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Value</Label>
              <Input
                type="number"
                step="0.01"
                value={form.value}
                onChange={(e) => setForm(p => ({ ...p, value: e.target.value }))}
                data-testid="simple-deal-value-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm(p => ({ ...p, stage: v }))}>
                <SelectTrigger data-testid="simple-deal-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prospecting">Prospecting</SelectItem>
                  <SelectItem value="qualification">Qualification</SelectItem>
                  <SelectItem value="proposal">Proposal</SelectItem>
                  <SelectItem value="negotiation">Negotiation</SelectItem>
                  <SelectItem value="closed_won">Closed Won</SelectItem>
                  <SelectItem value="closed_lost">Closed Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Expected Close Date</Label>
            <Input
              type="date"
              value={form.expected_close_date}
              onChange={(e) => setForm(p => ({ ...p, expected_close_date: e.target.value }))}
              data-testid="simple-deal-close-date-input"
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="simple-deal-submit-btn">
              {loading ? 'Creating...' : 'Create Deal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});
SimpleDealDialog.displayName = 'SimpleDealDialog';

export default {
  SimpleTicketDialog,
  SimpleSubscriptionDialog,
  AttachmentDialog,
  SimplePaymentDialog,
  CompanyDialog,
  SimpleDealDialog
};
